import { ApolloCache, Reference } from '@apollo/client';
import {
  DashboardUseCase,
  Scope,
  Status,
  UpdateDashboardMutationVariables,
} from 'generated/graphql';

export const updateCachedDashboard = (
  cache: ApolloCache<unknown>,
  _: unknown,
  options: { variables?: UpdateDashboardMutationVariables },
) => {
  const { variables } = options;
  if (!variables) {
    return;
  }

  cache.modify({
    id: cache.identify({
      __typename: 'Dashboard',
      id: variables.id,
    }),
    fields: {
      title: (existing: string) => variables.title ?? existing,
      scope: (existing: Scope) => variables.scope ?? existing,
      metadata: (existing: string) => variables.metadata ?? existing,
      shortMetadata: (existing: string) => variables.shortMetadata ?? existing,
      description: (existing: string) => variables.description ?? existing,
      status: (existing: Status) => variables.status ?? existing,
      useCase: (existing: DashboardUseCase) => variables.useCase ?? existing,
      analyses: (existing: Reference[]) => {
        if (
          !variables?.removeAnalysisIDs ||
          // gqlgen generates string array types as string[] | string
          typeof variables?.removeAnalysisIDs === 'string'
        ) {
          return existing;
        }
        const removedReferencesSet = new Set(
          variables?.removeAnalysisIDs.map(
            (id) =>
              cache.identify({
                __typename: 'Analysis',
                id,
              }) ?? '',
          ),
        );
        return existing?.filter(
          ({ __ref }) => !removedReferencesSet.has(__ref),
        );
      },
      editors: (existing: Reference[]) => {
        if (
          !variables?.editorsToRemove ||
          // gqlgen generates string array types as string[] | string
          typeof variables?.editorsToRemove === 'string'
        ) {
          return existing;
        }
        const removedReferencesSet = new Set(
          variables?.editorsToRemove.map(
            (id) =>
              cache.identify({
                __typename: 'UserInfo',
                id,
              }) ?? '',
          ),
        );
        return existing?.filter(
          ({ __ref }) => !removedReferencesSet.has(__ref),
        );
      },
    },
  });

  cache.modify({
    id: cache.identify({
      __typename: 'DashboardListItem',
      id: variables.id,
    }),
    fields: {
      title: (existing: string) => variables.title ?? existing,
      scope: (existing: Scope) => variables.scope ?? existing,
      metadata: (existing: string) => variables.metadata ?? existing,
      status: (existing: Status) => variables.status ?? existing,
    },
  });
};
